import { useMutation } from '@tanstack/react-query'

import {
  type AppTimezonePreference,
  getBrowserDatePart,
  getInclusiveLocalDateUtcPeriodIso,
} from '@sparkai/utils'

import { apiClient } from '../apiClient'

type Condition = {
  type: string
  values: string[]
}

type EngagementPreviewMutation = {
  conditions: Condition[]
  dateOption: 'Recent' | 'From a specific date' | 'Specific dates'
  rangeInDays?: number
  startDate?: Date
  endDate?: Date
  specificDate?: Date
  accuracyMin?: number
  accuracyMax?: number
  auditedOnly?: boolean
  timezonePreference?: AppTimezonePreference
}

type EngagementPreviewRequest = {
  programId: string
  conditions: Condition[]
  startDate?: string
  endDate?: string
  accuracyMin?: number
  accuracyMax?: number
  auditedOnly?: boolean
}

function getDateRangePeriod(startDate: Date, endDate: Date, timezonePreference: AppTimezonePreference) {
  return getInclusiveLocalDateUtcPeriodIso(
    getBrowserDatePart(startDate),
    getBrowserDatePart(endDate),
    timezonePreference
  )
}

export function useCreatePreview(programId?: string) {
  return useMutation({
    mutationFn: async ({
      conditions,
      dateOption,
      accuracyMin,
      accuracyMax,
      auditedOnly,
      timezonePreference,
      ...args
    }: EngagementPreviewMutation) => {
      if (!programId) {
        throw new Error('programId is required')
      }

      const requestParams: EngagementPreviewRequest = {
        programId,
        conditions,
        accuracyMin,
        accuracyMax,
        auditedOnly,
      }

      if (dateOption === 'Recent' && args.rangeInDays) {
        const now = new Date()
        const startDate = new Date(now)
        startDate.setDate(now.getDate() - args.rangeInDays)
        requestParams.startDate = startDate.toISOString()
        requestParams.endDate = now.toISOString()
      } else if (dateOption === 'From a specific date') {
        const date = args.specificDate
        if (!date) return
        if (timezonePreference) {
          const period = getDateRangePeriod(date, date, timezonePreference)
          requestParams.startDate = period.start
          requestParams.endDate = period.end
        } else {
          requestParams.startDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            0,
            0,
            0
          ).toISOString()
          requestParams.endDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23,
            59,
            59
          ).toISOString()
        }
      } else if (dateOption === 'Specific dates') {
        if (timezonePreference && args.startDate && args.endDate) {
          const period = getDateRangePeriod(args.startDate, args.endDate, timezonePreference)
          requestParams.startDate = period.start
          requestParams.endDate = period.end
        } else {
          if (args.startDate) requestParams.startDate = args.startDate.toISOString()
          if (args.endDate) requestParams.endDate = args.endDate.toISOString()
        }
      } else {
        throw new Error('Need to specify a date option')
      }

      const { data } = await apiClient.post<{ count: number }>(`/int/v3/engagements/preview`, {
        ...requestParams,
      })

      return data
    },
  })
}
